import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LiveMap } from "@/components/LiveMap";
import { Loader } from "@/components/Loader";
import {
  ShieldCheck,
  ArrowLeft,
  MapPin,
  Clock,
  Car,
  Footprints,
  Bike,
  TramFront,
  Check,
  HandHelping,
  ChevronRight,
  BatteryLow,
  WifiOff,
  Route,
  Share2,
  BellRing,
  Lock,
  Phone,
  Sparkles,
  X,
  Copy,
  MessageCircle,
  Eye,
  Ban,
  Users,
  Volume2,
  VolumeX,
  Square,
  Play,
} from "lucide-react";

const MODES = [
  { id: "walk", label: "Walking", icon: Footprints },
  { id: "cab", label: "Cab / Taxi", icon: Car },
  { id: "auto", label: "Auto", icon: Car },
  { id: "bike", label: "Bike", icon: Bike },
  { id: "metro", label: "Metro / Bus", icon: TramFront },
];
const ETA_PRESETS = [10, 15, 20, 30, 45, 60];
const NUDGE_INTERVAL_S = 90; // discreet check-in cadence
const MISS_DEADLINE_S = 45; // grace before a missed check-in escalates

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

// ---------------------------------------------------------------- Start form
const StartForm = ({ onStarted }) => {
  const navigate = useNavigate();
  const [destination, setDestination] = useState("");
  const [eta, setEta] = useState(20);
  const [mode, setMode] = useState("cab");
  const [cabNumber, setCabNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [notes, setNotes] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [silent, setSilent] = useState(false);
  const [coords, setCoords] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/trusted-contacts").then((r) => {
      setContacts(r.data || []);
      setSelected((r.data || []).map((c) => c.id));
    }).catch(() => setContacts([]));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => setCoords(null),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, []);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const start = async () => {
    if (!destination.trim()) return toast.error("Where are you heading?");
    setBusy(true);
    try {
      const { data } = await api.post("/journeys/start", {
        destination: destination.trim(),
        eta_minutes: eta,
        mode,
        cab_number: cabNumber,
        driver_name: driverName,
        notes,
        contact_ids: selected,
        default_silent: silent,
        start_lat: coords?.lat ?? null,
        start_lng: coords?.lng ?? null,
      });
      toast.success("Guardian is now watching over your trip");
      onStarted(data);
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const needsVehicle = mode === "cab" || mode === "auto";

  return (
    <div className="ll-fade-up pb-10" data-testid="journey-start-form">
      <div className="bg-[#0E7C66] text-white px-5 pt-8 pb-10 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <button data-testid="journey-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/90 text-sm font-semibold mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3 relative">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black font-head leading-tight">Guardian Journey</h1>
            <p className="text-white/85 text-sm">A calm companion for your trip home.</p>
          </div>
        </div>
      </div>

      <div className="px-5 -mt-5 space-y-4">
        <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-[#111827]/5 space-y-5">
          <label className="block">
            <span className="text-sm font-bold text-[#111827] flex items-center gap-1.5 mb-2"><MapPin className="w-4 h-4 text-[#0E7C66]" /> Where are you going?</span>
            <input data-testid="journey-destination" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. Home, Govindpur" className="w-full rounded-2xl border border-[#111827]/12 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0E7C66]/40" />
          </label>

          <div>
            <span className="text-sm font-bold text-[#111827] flex items-center gap-1.5 mb-2"><Clock className="w-4 h-4 text-[#0E7C66]" /> Expected to arrive in</span>
            <div className="flex flex-wrap gap-2">
              {ETA_PRESETS.map((m) => (
                <button key={m} data-testid={`eta-${m}`} onClick={() => setEta(m)} className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${eta === m ? "bg-[#0E7C66] text-white border-[#0E7C66]" : "bg-white text-[#111827] border-[#111827]/15"}`}>{m} min</button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-sm font-bold text-[#111827] mb-2 block">How are you travelling?</span>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)} className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border text-xs font-bold transition-colors ${mode === m.id ? "bg-[#0E7C66]/10 border-[#0E7C66] text-[#0E7C66]" : "bg-white border-[#111827]/12 text-[#111827]/70"}`}>
                  <m.icon className="w-5 h-5" /> {m.label}
                </button>
              ))}
            </div>
          </div>

          {needsVehicle && (
            <div className="grid grid-cols-2 gap-3 ll-fade-up">
              <label className="block">
                <span className="text-xs font-bold text-[#111827]/70 mb-1 block">Vehicle number</span>
                <input data-testid="journey-cab-number" value={cabNumber} onChange={(e) => setCabNumber(e.target.value)} placeholder="JH05 AB 1234" className="w-full rounded-2xl border border-[#111827]/12 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7C66]/40" />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-[#111827]/70 mb-1 block">Driver name</span>
                <input data-testid="journey-driver-name" value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Optional" className="w-full rounded-2xl border border-[#111827]/12 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7C66]/40" />
              </label>
            </div>
          )}

          <label className="block">
            <span className="text-xs font-bold text-[#111827]/70 mb-1 block">Anything else? (optional)</span>
            <input data-testid="journey-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Feeling uneasy, please stay close" className="w-full rounded-2xl border border-[#111827]/12 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7C66]/40" />
          </label>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-[#111827]/5">
          <span className="text-sm font-bold text-[#111827] flex items-center gap-1.5 mb-1"><HandHelping className="w-4 h-4 text-[#0E7C66]" /> Who should watch over you?</span>
          <p className="text-xs text-[#111827]/50 mb-3">Only these people can see your live trip — and only while it is active.</p>
          {contacts.length === 0 ? (
            <button data-testid="journey-add-contact" onClick={() => navigate("/profile/trusted-contacts")} className="w-full text-sm font-bold text-[#0E7C66] bg-[#0E7C66]/8 rounded-2xl py-3">
              + Add a trusted contact first
            </button>
          ) : (
            <div className="space-y-2">
              {contacts.map((c) => {
                const on = selected.includes(c.id);
                return (
                  <button key={c.id} data-testid={`contact-${c.id}`} onClick={() => toggle(c.id)} className={`w-full flex items-center gap-3 p-2.5 rounded-2xl border transition-colors ${on ? "border-[#0E7C66] bg-[#0E7C66]/6" : "border-[#111827]/10"}`}>
                    <div className="w-9 h-9 rounded-full bg-[#C4B5FD]/30 flex items-center justify-center font-bold">{c.name[0]}</div>
                    <div className="flex-1 text-left"><p className="font-semibold text-sm">{c.name}</p><p className="text-xs text-[#111827]/50">{c.relationship}</p></div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${on ? "bg-[#0E7C66]" : "bg-[#111827]/10"}`}>{on && <Check className="w-4 h-4 text-white" />}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button data-testid="journey-silent-toggle" onClick={() => setSilent((s) => !s)} className="w-full bg-white rounded-3xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-[#111827]/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#111827]/5 flex items-center justify-center"><Lock className="w-5 h-5 text-[#111827]/70" /></div>
          <div className="flex-1 text-left"><p className="font-bold text-sm">Discreet mode</p><p className="text-xs text-[#111827]/50">Quiet, low-key screen. No loud alerts.</p></div>
          <div className={`w-11 h-6 rounded-full transition-colors relative ${silent ? "bg-[#0E7C66]" : "bg-[#111827]/15"}`}>
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${silent ? "left-5" : "left-0.5"}`} />
          </div>
        </button>

        <div className="flex items-start gap-2 text-xs text-[#111827]/50 px-1">
          <ShieldCheck className="w-4 h-4 shrink-0 text-[#0E7C66]" />
          <p>Your location is shared only while this journey is active and is removed when you arrive. HumNest coordinates neighbours — it does not contact police or ambulance for you.</p>
        </div>

        <button data-testid="journey-start-btn" disabled={busy} onClick={start} className="w-full bg-[#0E7C66] text-white rounded-3xl py-4 text-lg font-black shadow-lg active:scale-[0.98] transition-transform disabled:opacity-60">
          {busy ? "Starting…" : "Start Guardian Journey"}
        </button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------ Live monitoring
const Live = ({ initial, onEnded }) => {
  const navigate = useNavigate();
  const [j, setJ] = useState(initial);
  const [remainingS, setRemainingS] = useState(0);
  const [nudgeIn, setNudgeIn] = useState(NUDGE_INTERVAL_S);
  const [awaiting, setAwaiting] = useState(false);
  const [missIn, setMissIn] = useState(MISS_DEADLINE_S);
  const [online, setOnline] = useState(navigator.onLine);
  const [battery, setBattery] = useState(null);
  const [gpsError, setGpsError] = useState(false);
  const [sosArming, setSosArming] = useState(null); // 'audible' pending confirm
  const [shareOpen, setShareOpen] = useState(false);
  const { user } = useAuth();
  const lastSentRef = useRef(0);
  const jidRef = useRef(initial.id);
  const audioRef = useRef(null);
  const [alarm, setAlarm] = useState({ active: false, muted: false, blocked: false });
  const vibrationSupported = typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
  const silent = j.silent;
  const silentActive = j.status === "emergency" && j.emergency_mode === "silent";
  const emergency = j.status === "emergency" && !silentActive; // visible emergency only
  const activeLike = j.status === "active" || silentActive;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/journeys/${jidRef.current}`);
      setJ(data);
    } catch {
      /* keep last known state if the network drops */
    }
  }, []);

  // poll server every 5s
  useEffect(() => {
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // online/offline + battery
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    let batt;
    if (navigator.getBattery) {
      navigator.getBattery().then((b) => {
        batt = b;
        const read = () => setBattery(Math.round(b.level * 100));
        read();
        b.addEventListener("levelchange", read);
      });
    }
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      if (batt) batt.onlevelchange = null;
    };
  }, []);

  // location watch → throttled sync (privacy: only while active)
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setGpsError(false);
        const nowMs = Date.now();
        if (nowMs - lastSentRef.current < 8000) return;
        lastSentRef.current = nowMs;
        api
          .post(`/journeys/${jidRef.current}/location`, {
            lat: p.coords.latitude,
            lng: p.coords.longitude,
            battery: battery == null ? undefined : battery,
            online: navigator.onLine,
            route_deviation: false,
          })
          .then((r) => setJ(r.data))
          .catch(() => {});
      },
      () => setGpsError(true),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [battery]);

  // 1s ticker: ETA countdown + check-in nudge + missed-check-in escalation
  useEffect(() => {
    const planned = new Date(j.planned_arrival).getTime();
    const t = setInterval(() => {
      setRemainingS(Math.max(0, Math.round((planned - Date.now()) / 1000)));
      if (j.status !== "active") return;
      setNudgeIn((n) => {
        if (n <= 1 && !awaiting) {
          setAwaiting(true);
          setMissIn(MISS_DEADLINE_S);
          return NUDGE_INTERVAL_S;
        }
        return n <= 1 ? NUDGE_INTERVAL_S : n - 1;
      });
      if (awaiting) {
        setMissIn((m) => {
          if (m <= 1) {
            handleMissed();
            return MISS_DEADLINE_S;
          }
          return m - 1;
        });
      }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [j.planned_arrival, j.status, awaiting]);

  const handleMissed = async () => {
    setAwaiting(false);
    try {
      const { data } = await api.post(`/journeys/${jidRef.current}/escalate`);
      setJ(data);
      if (!silent) toast.warning("We didn't hear back — your circle has been alerted.");
    } catch {}
  };

  const doCheckin = async (response) => {
    setAwaiting(false);
    setNudgeIn(NUDGE_INTERVAL_S);
    try {
      const { data } = await api.post(`/journeys/${jidRef.current}/checkin`, { response });
      setJ(data);
      if (response === "yes" && !silent) toast.success("Glad you're safe. Guardian is still with you.");
      if (response === "need_help") toast.warning("Reaching out to your circle now.");
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    }
  };

  // ---- Audible SOS alarm (Web Audio API — no asset/library). Must start from a user tap. ----
  const startAlarm = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) { setAlarm({ active: true, muted: false, blocked: true }); return; }
      const ctx = new Ctx();
      const master = ctx.createGain();
      master.gain.value = 1;
      master.connect(ctx.destination);
      const beep = () => {
        if (ctx.state !== "running") return;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "square";
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.frequency.setValueAtTime(660, ctx.currentTime + 0.25);
        g.gain.setValueAtTime(0.8, ctx.currentTime);
        o.connect(g); g.connect(master);
        o.start();
        o.stop(ctx.currentTime + 0.45);
      };
      const interval = setInterval(beep, 900);
      let vibInterval = null;
      const pattern = [500, 250, 500, 250, 800];
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
        vibInterval = setInterval(() => navigator.vibrate(pattern), 2400);
      }
      audioRef.current = { ctx, master, interval, vibInterval };
      const finish = () => {
        beep();
        setAlarm({ active: true, muted: false, blocked: ctx.state !== "running" });
      };
      if (ctx.state === "suspended") ctx.resume().then(finish).catch(finish);
      else finish();
    } catch {
      setAlarm({ active: true, muted: false, blocked: true });
    }
  };

  const stopAlarm = () => {
    const a = audioRef.current;
    if (a) {
      clearInterval(a.interval);
      if (a.vibInterval) clearInterval(a.vibInterval);
      if (navigator.vibrate) navigator.vibrate(0);
      try { a.master.disconnect(); a.ctx.close(); } catch {}
      audioRef.current = null;
    }
    setAlarm({ active: false, muted: false, blocked: false });
  };

  const toggleMute = () => {
    const a = audioRef.current;
    setAlarm((s) => {
      const muted = !s.muted;
      if (a) a.master.gain.value = muted ? 0 : 1;
      if (muted && navigator.vibrate) navigator.vibrate(0);
      return { ...s, muted };
    });
  };

  // stop any alarm when the component unmounts
  useEffect(() => () => stopAlarm(), []);

  const triggerSos = async (mode) => {
    setSosArming(null);
    try {
      const { data } = await api.post(`/journeys/${jidRef.current}/sos`, { mode, origin: window.location.origin });
      setJ(data);
      if (mode === "silent") {
        // discreet — no visible alarm, no sound, no vibration
      } else {
        toast.error("SOS active. Your trusted circle is being alerted.");
      }
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    }
  };

  // Audible SOS confirm: start the alarm within the tap gesture, then create the SOS.
  const confirmAudible = () => {
    startAlarm();
    triggerSos("audible");
  };

  const endJourney = async (kind) => {
    stopAlarm();
    try {
      const { data } = await api.post(`/journeys/${jidRef.current}/${kind}`);
      setJ(data);
      toast.success(kind === "arrive" ? "Arrived safely 🎉 Journey closed." : "Journey ended. Location sharing stopped.");
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const center = j.last_lat != null ? [j.last_lat, j.last_lng] : null;
  const markers = [];
  if (center) markers.push({ id: "you", lat: j.last_lat, lng: j.last_lng, type: "you", pulse: true, label: "You", sub: j.destination });
  if (j.responder?.lat && !silentActive) markers.push({ id: "r", lat: j.responder.lat, lng: j.responder.lng, type: "responder", letter: j.responder.name?.[0], label: j.responder.name, sub: `Guardian · ${j.responder.eta_min} min away` });
  const routes = center && j.responder?.lat && !silentActive ? [[[j.responder.lat, j.responder.lng], [j.last_lat, j.last_lng]]] : [];

  const accent = emergency ? "#DC2626" : j.escalation_level > 0 && !silentActive ? "#F97316" : "#0E7C66";
  const statusLabel = silentActive ? "On the way" : j.status_label;

  return (
    <div className="ll-fade-up pb-10" data-testid="journey-live">
      {/* Header */}
      <div className="px-5 pt-8 pb-6 rounded-b-[2rem] text-white relative overflow-hidden transition-colors" style={{ background: accent }}>
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        {silentActive && <span data-testid="silent-active-indicator" title="Guardian active" className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white/40" />}
        <button data-testid="journey-minimize" onClick={() => navigate("/home")} className="flex items-center gap-1 text-white/90 text-sm font-semibold mb-4">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            {emergency ? <BellRing className="w-7 h-7" /> : <ShieldCheck className="w-7 h-7" />}
          </div>
          <div className="flex-1">
            <p className="text-white/80 text-xs font-bold uppercase tracking-wider" data-testid="journey-status">{statusLabel}</p>
            <h1 className="text-xl font-black font-head leading-tight">{j.destination}</h1>
          </div>
          {activeLike && (
            <div className="text-right">
              <p className="text-2xl font-black tabular-nums" data-testid="journey-eta">{mmss(remainingS)}</p>
              <p className="text-white/80 text-xs">ETA</p>
            </div>
          )}
        </div>
        {/* progress */}
        <div className="relative mt-4 h-2 rounded-full bg-white/25 overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${Math.round((j.progress || 0) * 100)}%` }} />
        </div>
      </div>

      <div className="px-5 -mt-3 space-y-4">
        {/* Resilience banners */}
        {(activeLike || emergency) && (
          <>
            {!online && (
              <div data-testid="banner-offline" className="bg-[#111827] text-white rounded-2xl p-3 flex items-center gap-2 text-sm font-semibold">
                <WifiOff className="w-4 h-4" /> You're offline. Your last known location is kept safe and will resync automatically.
              </div>
            )}
            {gpsError && (
              <div data-testid="banner-gps" className="bg-[#F59E0B]/15 text-[#92400e] rounded-2xl p-3 flex items-center gap-2 text-sm font-semibold">
                <MapPin className="w-4 h-4" /> Weak GPS signal — sharing your last confirmed location.
              </div>
            )}
            {battery != null && battery <= 20 && (
              <div data-testid="banner-battery" className="bg-[#F59E0B]/15 text-[#92400e] rounded-2xl p-3 flex items-center gap-2 text-sm font-semibold">
                <BatteryLow className="w-4 h-4" /> Battery low ({battery}%). Guardian keeps sharing your last location if your phone dies.
              </div>
            )}
            {j.route_deviation && (
              <div data-testid="banner-route" className="bg-[#F59E0B]/15 text-[#92400e] rounded-2xl p-3 flex items-center gap-2 text-sm font-semibold">
                <Route className="w-4 h-4" /> Route looks different than expected. Everything okay?
              </div>
            )}
          </>
        )}

        {/* Map */}
        {(activeLike || emergency) &&
          (center ? (
            <LiveMap center={center} zoom={15} height={200} markers={markers} routes={routes} testId="journey-map" />
          ) : (
            <div className="bg-white rounded-3xl p-6 text-center text-sm text-[#111827]/50 border border-[#111827]/5" data-testid="journey-map-empty">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-[#111827]/30" />
              Waiting for location… Guardian is still monitoring your check-ins.
            </div>
          ))}

        {/* Escalation status */}
        {j.escalation_level > 0 && !silentActive && (
          <div data-testid="journey-escalation" className="bg-white rounded-3xl p-4 border border-[#F97316]/30 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#F97316]/12 flex items-center justify-center"><BellRing className="w-5 h-5 text-[#F97316]" /></div>
            <div className="flex-1"><p className="font-bold text-sm">{j.escalation_label}</p><p className="text-xs text-[#111827]/50">Guardian raised your protection level.</p></div>
          </div>
        )}

        {/* Responder */}
        {j.responder && !silentActive && (
          <div data-testid="journey-responder" className="bg-white rounded-3xl p-4 border border-[#111827]/5 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#F97316] text-white flex items-center justify-center font-black">{j.responder.name?.[0]}</div>
            <div className="flex-1">
              <p className="font-bold text-sm flex items-center gap-1">{j.responder.name} <ShieldCheck className="w-3.5 h-3.5 text-[#0E7C66]" /></p>
              <p className="text-xs text-[#111827]/50">Verified guardian · {j.responder.distance_km} km · ~{j.responder.eta_min} min away</p>
            </div>
          </div>
        )}

        {/* Check-in card */}
        {activeLike && (
          <div data-testid="journey-checkin" className={`rounded-3xl p-5 border transition-colors ${awaiting ? "bg-[#0E7C66]/6 border-[#0E7C66]" : "bg-white border-[#111827]/5"}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="font-bold text-sm flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#0E7C66]" /> Safety check-in</p>
              <span className="text-xs text-[#111827]/45" data-testid="checkin-timer">
                {awaiting ? `Please respond · ${missIn}s` : `Next nudge in ${nudgeIn}s`}
              </span>
            </div>
            <p className="text-xs text-[#111827]/50 mb-3">A gentle nudge now and then. Silence tells Guardian something may be wrong.</p>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="checkin-yes" onClick={() => doCheckin("yes")} className="flex flex-col items-center gap-1 py-3 rounded-2xl bg-[#0E7C66] text-white font-bold text-sm active:scale-95 transition-transform"><Check className="w-5 h-5" /> I'm safe</button>
              <button data-testid="checkin-help" onClick={() => doCheckin("need_help")} className="flex flex-col items-center gap-1 py-3 rounded-2xl bg-[#F97316] text-white font-bold text-sm active:scale-95 transition-transform"><HandHelping className="w-5 h-5" /> Need help</button>
              <button data-testid="checkin-skip" onClick={() => doCheckin("skip")} className="flex flex-col items-center gap-1 py-3 rounded-2xl bg-[#111827]/5 text-[#111827]/70 font-bold text-sm active:scale-95 transition-transform"><ChevronRight className="w-5 h-5" /> Later</button>
            </div>
          </div>
        )}

        {/* Trip details */}
        {(j.cab_number || j.driver_name) && (
          <div className="bg-white rounded-3xl p-4 border border-[#111827]/5" data-testid="journey-trip-details">
            <p className="text-xs font-bold text-[#111827]/50 mb-2 uppercase tracking-wide">Trip details (shared with your circle)</p>
            <div className="flex items-center gap-4 text-sm">
              {j.cab_number && <span className="flex items-center gap-1.5 font-semibold"><Car className="w-4 h-4 text-[#111827]/50" /> {j.cab_number}</span>}
              {j.driver_name && <span className="flex items-center gap-1.5 font-semibold"><Phone className="w-4 h-4 text-[#111827]/50" /> {j.driver_name}</span>}
            </div>
          </div>
        )}

        {/* Share */}
        {(activeLike || emergency) && (
          <button data-testid="journey-share" onClick={() => setShareOpen(true)} className="w-full bg-white rounded-3xl p-4 border border-[#111827]/5 flex items-center gap-3 active:scale-[0.99] transition-transform">
            <div className="w-10 h-10 rounded-2xl bg-[#C4B5FD]/25 flex items-center justify-center"><Share2 className="w-5 h-5 text-[#4c1d95]" /></div>
            <div className="flex-1 text-left"><p className="font-bold text-sm">Share live trip link</p><p className="text-xs text-[#111827]/50">{j.share_active ? "Sharing on · tap to manage" : "A private, read-only view. No login needed."}</p></div>
            <ChevronRight className="w-5 h-5 text-[#111827]/30" />
          </button>
        )}

        {/* SOS */}
        {emergency ? (
          <div data-testid="journey-emergency-active" className="bg-[#DC2626]/8 border border-[#DC2626]/30 rounded-3xl p-4 text-center">
            <BellRing className="w-8 h-8 text-[#DC2626] mx-auto mb-1" />
            <p className="font-black text-[#DC2626]">SOS active</p>
            <p className="text-xs text-[#111827]/60 mt-1">Alerting your trusted circle. HumNest does not contact police or ambulance. Stay where you are if it is safe.</p>

            {j.emergency_mode !== "silent" && (
              <div className="mt-3" data-testid="alarm-controls">
                {alarm.blocked && (
                  <p className="text-xs text-[#92400e] bg-[#F59E0B]/15 rounded-xl p-2.5 mb-2" data-testid="alarm-blocked-note">Your browser blocked the alarm sound. Increase volume and tap Play Alarm.</p>
                )}
                {alarm.active ? (
                  <div className="grid grid-cols-2 gap-3">
                    <button data-testid="alarm-stop" onClick={stopAlarm} className="rounded-2xl py-3 bg-[#111827] text-white font-black flex items-center justify-center gap-2"><Square className="w-4 h-4" /> Stop alarm</button>
                    <button data-testid="alarm-mute" onClick={toggleMute} className="rounded-2xl py-3 bg-white border border-[#111827]/15 text-[#111827] font-black flex items-center justify-center gap-2">{alarm.muted ? <><Volume2 className="w-4 h-4" /> Unmute</> : <><VolumeX className="w-4 h-4" /> Mute</>}</button>
                  </div>
                ) : (
                  <button data-testid="alarm-play" onClick={startAlarm} className="w-full rounded-2xl py-3 bg-[#DC2626] text-white font-black flex items-center justify-center gap-2"><Play className="w-4 h-4" /> Play alarm</button>
                )}
                {!vibrationSupported && <p className="text-[11px] text-[#111827]/45 mt-2" data-testid="vibration-note">Vibration is not supported on this device.</p>}
              </div>
            )}

            <button data-testid="sos-false-alarm" onClick={() => endJourney("cancel")} className="mt-3 w-full bg-white border border-[#DC2626]/30 text-[#DC2626] rounded-2xl py-3 font-bold">
              I'm safe — it was a false alarm
            </button>
            <AlertLog jid={jidRef.current} />
          </div>
        ) : activeLike ? (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button data-testid="sos-silent" onClick={() => triggerSos("silent")} className="rounded-3xl py-4 bg-[#111827] text-white font-black flex flex-col items-center gap-1 active:scale-95 transition-transform">
              <Lock className="w-6 h-6" /> Silent SOS
              <span className="text-[10px] font-medium opacity-70">Discreet, no sound</span>
            </button>
            <button data-testid="sos-audible" onClick={() => setSosArming("audible")} className="rounded-3xl py-4 bg-[#DC2626] text-white font-black flex flex-col items-center gap-1 active:scale-95 transition-transform">
              <BellRing className="w-6 h-6" /> Audible SOS
              <span className="text-[10px] font-medium opacity-80">Alert everyone</span>
            </button>
          </div>
        ) : null}

        {/* End journey */}
        {activeLike && (
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="journey-arrive" onClick={() => endJourney("arrive")} className="rounded-3xl py-4 bg-[#16A34A] text-white font-black active:scale-95 transition-transform flex items-center justify-center gap-2"><Check className="w-5 h-5" /> Arrived safely</button>
            <button data-testid="journey-cancel" onClick={() => endJourney("cancel")} className="rounded-3xl py-4 bg-white border border-[#111827]/12 text-[#111827]/70 font-black active:scale-95 transition-transform">End journey</button>
          </div>
        )}

        {(j.status === "arrived" || j.status === "cancelled") && (
          <div data-testid="journey-closed" className="bg-white rounded-3xl p-6 text-center border border-[#111827]/5">
            <div className="w-14 h-14 rounded-full bg-[#16A34A]/12 flex items-center justify-center mx-auto mb-3"><Check className="w-8 h-8 text-[#16A34A]" /></div>
            <p className="font-black text-lg">{j.status === "arrived" ? "You arrived safely" : "Journey ended"}</p>
            <p className="text-sm text-[#111827]/50 mt-1">Live location sharing has stopped.</p>
            <button data-testid="journey-new" onClick={onEnded} className="mt-4 w-full bg-[#0E7C66] text-white rounded-2xl py-3 font-bold">Start a new journey</button>
          </div>
        )}
      </div>

      {/* Audible SOS confirm (accidental-trigger guard) */}
      {sosArming && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="sos-confirm">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm ll-fade-up">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-black flex items-center gap-2 text-[#DC2626]"><BellRing className="w-5 h-5" /> Send Audible SOS?</h3>
              <button data-testid="sos-confirm-close" onClick={() => setSosArming(null)}><X className="w-5 h-5 text-[#111827]/40" /></button>
            </div>
            <p className="text-sm text-[#111827]/60 mb-4">This starts a loud alarm and alerts your trusted circle. HumNest does not contact police or ambulance. Use only if you feel unsafe.</p>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="sos-confirm-cancel" onClick={() => setSosArming(null)} className="rounded-2xl py-3 bg-[#111827]/5 font-bold">Cancel</button>
              <button data-testid="sos-confirm-yes" onClick={confirmAudible} className="rounded-2xl py-3 bg-[#DC2626] text-white font-black">Yes, send SOS</button>
            </div>
          </div>
        </div>
      )}

      {shareOpen && (
        <ShareSheet
          jid={jidRef.current}
          journey={j}
          userName={(user?.full_name || "A traveller").split(" ")[0]}
          onChange={refresh}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------- Share sheet
const WHATSAPP_MSG = (name, link) =>
  `${name} has started a Guardian Journey and has chosen to share this private trip link with you. You can follow the journey until it ends. No app installation is required.\n${link}`;

const ShareSheet = ({ jid, journey, userName, onChange, onClose }) => {
  const [viewers, setViewers] = useState({ viewers: [], active_count: 0, total: 0 });
  const [contacts, setContacts] = useState([]);
  const [pendingPrecise, setPendingPrecise] = useState(false);
  const [busy, setBusy] = useState(false);

  const active = !!journey.share_active;
  const precise = !!journey.share_precise;
  const token = journey.share_token;
  const link = `${window.location.origin}/journey/track/${token}`;

  const fetchViewers = useCallback(() => {
    api.get(`/journeys/${jid}/share/viewers`).then((r) => setViewers(r.data)).catch(() => {});
  }, [jid]);

  useEffect(() => {
    api.get("/trusted-contacts").then((r) => setContacts(r.data || [])).catch(() => setContacts([]));
    fetchViewers();
    const t = setInterval(fetchViewers, 8000);
    return () => clearInterval(t);
  }, [fetchViewers]);

  const recipients = contacts.filter((c) => (journey.contact_ids || []).includes(c.id));

  const enable = async () => {
    setBusy(true);
    try {
      await api.post(`/journeys/${jid}/share/enable`, { precise: pendingPrecise });
      await onChange();
      fetchViewers();
      toast.success("Trusted-trip link is on.");
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setPrecision = async (val) => {
    try {
      await api.post(`/journeys/${jid}/share/precision`, { precise: val });
      await onChange();
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/journeys/${jid}/share/revoke`);
      await onChange();
      setViewers({ viewers: [], active_count: 0, total: 0 });
      toast.success("Sharing stopped. The link no longer works.");
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    const done = () => toast.success("Link copied.");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(() => window.prompt("Copy your trusted link:", link));
    } else {
      window.prompt("Copy your trusted link:", link);
    }
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(WHATSAPP_MSG(userName, link))}`, "_blank");
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: WHATSAPP_MSG(userName, link) });
      } catch {
        /* user dismissed */
      }
    } else {
      copy();
    }
  };

  const expiresLabel = journey.share_expires_at
    ? new Date(journey.share_expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "arrival";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-3" data-testid="share-sheet">
      <div className="bg-[#FFF7E6] rounded-3xl w-full max-w-md ll-fade-up max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0E7C66] text-white px-5 py-4 rounded-t-3xl flex items-center justify-between">
          <h3 className="text-lg font-black flex items-center gap-2"><Share2 className="w-5 h-5" /> Share your trip privately</h3>
          <button data-testid="share-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Recipients */}
          <div className="bg-white rounded-2xl p-4 border border-[#111827]/5">
            <p className="text-xs font-bold uppercase tracking-wide text-[#111827]/45 mb-2 flex items-center gap-1.5"><Users className="w-4 h-4" /> Who receives this</p>
            {recipients.length ? (
              <div className="flex flex-wrap gap-2" data-testid="share-recipients">
                {recipients.map((c) => (
                  <span key={c.id} className="text-sm font-semibold bg-[#0E7C66]/8 text-[#0E7C66] px-3 py-1 rounded-full">{c.name}</span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#111827]/55" data-testid="share-recipients">Anyone you send the link to. Only share with people you trust.</p>
            )}
          </div>

          {/* What they see */}
          <div className="bg-white rounded-2xl p-4 border border-[#111827]/5">
            <p className="text-xs font-bold uppercase tracking-wide text-[#111827]/45 mb-2 flex items-center gap-1.5"><Eye className="w-4 h-4" /> What they can see</p>
            <ul className="text-sm text-[#111827]/70 space-y-1">
              <li>• Your first name &amp; live journey status</li>
              <li>• ETA, progress and last update time</li>
              <li>• {precise ? "Your exact live location" : "Your approximate area (not exact address)"}</li>
              <li>• Safety check-in status &amp; arrival confirmation</li>
            </ul>
            <p className="text-xs text-[#111827]/45 mt-2">They will never see your phone number, home address, contacts, or history.</p>
          </div>

          {/* Precision toggle */}
          <button
            data-testid="share-precision-toggle"
            onClick={() => (active ? setPrecision(!precise) : setPendingPrecise((v) => !v))}
            className="w-full bg-white rounded-2xl p-4 border border-[#111827]/5 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-2xl bg-[#0E7C66]/8 flex items-center justify-center"><MapPin className="w-5 h-5 text-[#0E7C66]" /></div>
            <div className="flex-1 text-left">
              <p className="font-bold text-sm">Share exact location</p>
              <p className="text-xs text-[#111827]/50">{(active ? precise : pendingPrecise) ? "Precise GPS is shared" : "Only an approximate area is shared"}</p>
            </div>
            <div className={`w-11 h-6 rounded-full transition-colors relative ${(active ? precise : pendingPrecise) ? "bg-[#0E7C66]" : "bg-[#111827]/15"}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${(active ? precise : pendingPrecise) ? "left-5" : "left-0.5"}`} />
            </div>
          </button>

          {/* Expiry note */}
          <p className="text-xs text-[#111827]/50 flex items-center gap-1.5 px-1"><Clock className="w-3.5 h-3.5" /> The link stops working when you arrive{journey.share_expires_at ? `, or by ${expiresLabel}` : ""}. You can stop sharing anytime.</p>

          {!active ? (
            <button data-testid="share-enable" disabled={busy} onClick={enable} className="w-full bg-[#0E7C66] text-white rounded-2xl py-3.5 font-black disabled:opacity-60">
              {busy ? "Turning on…" : "Turn on trusted sharing"}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="bg-white rounded-2xl p-3 border border-[#111827]/5 flex items-center gap-2">
                <p className="flex-1 text-xs text-[#111827]/60 truncate" data-testid="share-link">{link}</p>
                <button data-testid="share-copy" onClick={copy} className="text-[#0E7C66] font-bold text-sm flex items-center gap-1"><Copy className="w-4 h-4" /> Copy</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="share-whatsapp" onClick={whatsapp} className="rounded-2xl py-3 bg-[#25D366] text-white font-black flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp</button>
                <button data-testid="share-native" onClick={nativeShare} className="rounded-2xl py-3 bg-[#111827] text-white font-black flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Share…</button>
              </div>

              {/* Viewers / access log */}
              <div className="bg-white rounded-2xl p-4 border border-[#111827]/5" data-testid="share-viewers">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#111827]/45 flex items-center gap-1.5"><Eye className="w-4 h-4" /> Who's watching</p>
                  <span className="text-xs font-bold text-[#0E7C66]">{viewers.active_count} active · {viewers.total} total</span>
                </div>
                {viewers.viewers.length === 0 ? (
                  <p className="text-sm text-[#111827]/45">No one has opened the link yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {viewers.viewers.slice(0, 6).map((v) => (
                      <div key={v.vid} className="flex items-center gap-2 text-sm">
                        <span className={`w-2 h-2 rounded-full ${v.active ? "bg-[#16A34A]" : "bg-[#111827]/20"}`} />
                        <span className="font-mono text-xs text-[#111827]/60">Viewer {v.vid.slice(0, 6)}</span>
                        <span className="text-xs text-[#111827]/40 ml-auto">{v.views} view{v.views > 1 ? "s" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button data-testid="share-revoke" disabled={busy} onClick={revoke} className="w-full rounded-2xl py-3.5 bg-[#DC2626]/8 text-[#DC2626] font-black flex items-center justify-center gap-2 disabled:opacity-60">
                <Ban className="w-5 h-5" /> Stop sharing
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Alert log
const CH_LABEL = { push: "Push", sms: "SMS", whatsapp: "WhatsApp", email: "Email" };

const AlertLog = ({ jid }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const f = () => api.get(`/journeys/${jid}/alerts`).then((r) => setData(r.data)).catch(() => {});
    f();
    const t = setInterval(f, 5000);
    return () => clearInterval(t);
  }, [jid]);
  if (!data) return null;
  return (
    <div data-testid="alert-log" className="mt-4 text-left">
      <p className="text-xs font-bold text-[#111827]/60 mb-2">
        Alerting your circle · {data.delivered} sent · {data.acknowledged} acknowledged
      </p>
      <div className="space-y-1.5">
        {data.alerts.length === 0 && <p className="text-xs text-[#111827]/40">Reaching your trusted contacts…</p>}
        {data.alerts.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-xs bg-white rounded-xl px-3 py-2 border border-[#111827]/5">
            <span className="font-bold">{CH_LABEL[a.channel] || a.channel}</span>
            <span className="text-[#111827]/50 truncate">{a.contact_name}</span>
            <span className="text-[9px] uppercase font-bold text-[#4c1d95] bg-[#C4B5FD]/40 px-1.5 py-0.5 rounded-full">{a.tier}</span>
            <span className="ml-auto">
              {a.ack_at ? (
                <span className="text-[#16A34A] font-bold">Seen</span>
              ) : (
                <span className="text-[#111827]/40 capitalize">{a.status === "simulated" ? "notified" : a.status}</span>
              )}
            </span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#111827]/40 mt-2">In-app alerts reach trusted contacts who are signed in. SMS, WhatsApp and email delivery are coming soon.</p>
    </div>
  );
};

// -------------------------------------------------------------------- Page
export default function GuardianJourney() {
  const [state, setState] = useState({ loading: true, journey: null });

  const load = useCallback(() => {
    setState({ loading: true, journey: null });
    api
      .get("/journeys/active")
      .then((r) => setState({ loading: false, journey: r.data && r.data.id ? r.data : null }))
      .catch(() => setState({ loading: false, journey: null }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading) return <Loader />;
  if (state.journey) return <Live initial={state.journey} onEnded={load} />;
  return <StartForm onStarted={(j) => setState({ loading: false, journey: j })} />;
}
